import { createRef, Component } from 'react';
import { ArdoqGraphComponent } from 'viewpointBuilder/components/SimpleGraph/ArdoqGraphComponentClone';
import { DiffMode, ViewIds } from '@ardoq/api-types';
import {
  GraphComponent,
  GraphViewerInputMode,
  ScrollBarVisibility,
} from '@ardoq/yfiles';
import { CollapsibleGroupsYGraphBuilderClone } from './CollapsibleGroupsYGraphBuilderClone';
import { onLayoutGraph } from '../../../tabview/blockDiagram/view/yFilesExtensions/onLayoutGraph';
import { configureLayout } from './configureLayoutClone';
import { getStyle } from 'viewpointBuilder/components/SimpleGraph/styleLoaderClone';
import { ArdoqNodeStyleClone } from './ArdoqNodeStyleClone';
import { SimpleGraphViewProps } from '../../types';
import styled from 'styled-components';
import { s32, colors } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';
import { SecondaryButton } from '@ardoq/button';
import { Icon } from '@ardoq/icons';
import { TopRightCornerButtonContainer } from '../atoms';
import { LABEL_BACKGROUND_CLASS } from './ArdoqLabelStyleClone';
import type { BlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import { connect } from '@ardoq/rxbeach';
import { isViewpointMode$ } from 'traversals/loadedGraph$';

const VIEW_ID = ViewIds.METAMODEL;

const ViewLabel = styled.div`
  ${header4};
  margin-top: ${s32};
  margin-bottom: -${s32};
`;

const configureRendering = (graphComponent: GraphComponent) => {
  const inputMode = graphComponent.inputMode as GraphViewerInputMode;
  inputMode.navigationInputMode.enabled = false;
  graphComponent.maximumZoom = 1.6;
  graphComponent.minimumZoom = 0.01;
  graphComponent.mouseWheelZoomFactor = 1.1;
  graphComponent.horizontalScrollBarPolicy = ScrollBarVisibility.HIDDEN;
  graphComponent.verticalScrollBarPolicy = ScrollBarVisibility.HIDDEN;
};

const defaultViewSettings = {
  layoutOrientation: 1,
  incomingDegreesOfRelationship: 0,
  outgoingDegreesOfRelationship: 0,
  depthOfRelationship: 6,
  showOnlyConnectedComponents: false,
  includeOutgoingReferenceTypeNames: ['@@all@@'],
  includeIncomingReferenceTypeNames: ['@@all@@'],
  activeDiffMode: DiffMode.SCENARIO,
  collapsedGroupIds: [],
  separateReferences: false,
  useNewGrouping: false,
  isLegendActive: false,
  onViewSettingsUpdate: () => {},
} satisfies BlockDiagramViewSettings;

type SimpleGraphViewWithModeProps = SimpleGraphViewProps & {
  isViewpointMode: boolean;
};

class SimpleGraphViewWithMode extends Component<SimpleGraphViewWithModeProps> {
  ardoqGraphComponentRef = createRef<ArdoqGraphComponent>();

  private graphBuilder: CollapsibleGroupsYGraphBuilderClone | null = null;
  constructor(props: SimpleGraphViewWithModeProps) {
    super(props);
  }
  buildGraph = (graphComponent: GraphComponent, clearEdges: boolean) => {
    const {
      viewModel: { graphNodes, graphEdges },
    } = this.props;

    this.graphBuilder =
      this.graphBuilder ||
      new CollapsibleGroupsYGraphBuilderClone(
        graphComponent.graph,
        getStyle,
        style => (style as ArdoqNodeStyleClone).size
      );
    if (clearEdges) {
      this.graphBuilder.clearEdges();
    }

    const nodes = Array.from(graphNodes.values());
    const edges = Array.from(graphEdges.values());

    const isEmpty = !nodes.length && !edges.length;
    const hasLayoutUpdate = this.graphBuilder.hasLayoutUpdate({
      nodes,
      edges,
    });

    this.graphBuilder.nodesSource = nodes;
    this.graphBuilder.edgesSource = edges;
    this.graphBuilder.updateGraph();

    graphComponent.selection.clear();

    return { isAboveLimit: false, isEmpty, canBypass: true, hasLayoutUpdate };
  };

  getContainer = () =>
    this.ardoqGraphComponentRef.current?.graphComponentContainer.current ??
    null;

  getGraphComponent = () =>
    this.ardoqGraphComponentRef.current?.graphComponent ?? null;

  protected getViewModel() {
    return this.props.viewModel;
  }

  componentDidUpdate() {
    if (!this.getGraphComponent()) {
      this.graphBuilder = null;
    }
  }

  render() {
    const {
      props: {
        viewSettings = defaultViewSettings,
        viewModel,
        viewInstanceId = 'traversalDialog',
        clearEdges = false,
        label,
        graphInterface,
        topRightCornerButton,
        hasZoomControls,
        isThumbnailView,
        shouldFitToContentOnLayout,
        graphInsets,
        i18nLocale,
        isViewpointMode,
      },
    } = this;

    return (
      <StyleWrapper className={`tab-pane ${VIEW_ID}Tab active`}>
        {label && <ViewLabel>{label}</ViewLabel>}
        <ArdoqGraphComponent
          viewId={VIEW_ID}
          viewInstanceId={viewInstanceId}
          ref={this.ardoqGraphComponentRef}
          viewModel={viewModel}
          isLegendActive={viewSettings.isLegendActive}
          buildGraph={graphComponent =>
            this.buildGraph(graphComponent, clearEdges)
          }
          enableStyles={true}
          configureLayout={graphComponent =>
            configureLayout(
              graphComponent,
              viewModel,
              viewSettings,
              shouldFitToContentOnLayout,
              graphInsets
            )
          }
          configureRendering={configureRendering}
          onLayoutGraph={onLayoutGraph(null)}
          graphInterface={graphInterface}
          hasZoomControls={hasZoomControls}
          isThumbnailView={isThumbnailView}
          i18nLocale={i18nLocale}
          isViewpointMode={isViewpointMode}
        />
        {topRightCornerButton && (
          <TopRightCornerButtonContainer>
            <SecondaryButton onClick={topRightCornerButton.onClick}>
              {topRightCornerButton.iconName && (
                <Icon iconName={topRightCornerButton.iconName} />
              )}
              {topRightCornerButton.label}
            </SecondaryButton>
          </TopRightCornerButtonContainer>
        )}
      </StyleWrapper>
    );
  }
}

export const SIMPLE_GRAPH_BACKGROUND_COLOR_CSS_VARIABLE = '--background-color';

const StyleWrapper = styled.div`
  .${LABEL_BACKGROUND_CLASS} {
    fill: var(
      ${SIMPLE_GRAPH_BACKGROUND_COLOR_CSS_VARIABLE},
      ${colors.white950}
    );
  }
`;

export const SimpleGraphView = connect(
  SimpleGraphViewWithMode,
  isViewpointMode$
);
